import React, { useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { errorToastId } from '../../lib/toastDedupe';

import { useUser } from '../../contexts/UserContext';
import { usePortal, PORTAL_TOKEN_COOKIE } from '../../contexts/PortalContext';
import { getCookie } from '../../lib/cookies';

import Home from '../pages/Home';
import Processing from '../pages/Processing';
import ProcessingAgreement from '../pages/ProcessingAgreement';
import Login from '../auth/Login';
import Verify from '../auth/Verify';
import RegisterV2 from '../auth/RegisterV2';
import CallCenterProvider from '../operations/onboardingSupport/callCenter/CallCenterProvider';
import ReferralLink from '../auth/ReferralLink';
import MagicLinkHandler from '../auth/MagicLinkHandler';
import Terms from '../pages/Terms';
import Privacy from '../pages/Privacy';

import Dashboard from '../dashboard/Dashboard';
import Users from '../users/Users';
import Admins from '../users/Admins';
import Invitations from '../users/Invitations';
import Providers from '../users/Providers';
import ParsedContentViewer from '../claim/ParsedContentViewer';
import Profile from '../profile/Profile';
import PaymentPlanSetupPage from '../paymentPlans/PaymentPlanSetupPage';
import PaymentPlanConfirmationPage from '../paymentPlans/PaymentPlanConfirmationPage';
import Call from '../calls/Call';
import RingCall from '../calls/RingCall';
import Questionnaire from '../questionnaire/Questionnaire';
import Quickbooks from '../quickbooks/Quickbooks';
import Calendar from '../calendar/Calendar';
import VaForms from '../tools/VaForms';
import FormFieldMapper from '../tools/FormFieldMapper';
import DbqWorkbench from '../tools/DbqWorkbench';
import DbqFormRenderer from '../tools/DbqFormRenderer';
import DbqTemplateDebug from '../tools/DbqTemplateDebug';
import UserDbqEditor from '../tools/UserDbqEditor';
import ToeManager from '../tools/ToeManager';
import VaCalls from '../dashboard/admin/VaCalls';
import VaPayments from '../dashboard/admin/VaPayments';
import ReferralPayouts from '../operations/referrals/ReferralPayouts';
import DocumentsPage from '../documents/DocumentsPage';
import SignDocument from '../documents/SignDocument';
import PartnerOnboarding from '../referral/PartnerOnboarding';
import EventLanding from '../events/EventLanding';
import VaFailedCallsQueue from '../dashboard/admin/VaFailedCallsQueue';
import VaRatesManager from '../dashboard/admin/VaRatesManager';
import NotificationSettingsPage from '../admin/NotificationSettingsPage';
import ClientOnboardingPage from '../operations/clientOnboarding/ClientOnboardingPage';
import CmAssignmentPage from '../operations/cmAssignment/CmAssignmentPage';
import MessagesPage from '../operations/messages/MessagesPage';
import AutomationsPage from '../admin/AutomationsPage';
import CadenceEditorPage from '../admin/CadenceEditorPage';
import { homePathFor } from '../../lib/homePath';
import ClaimsManagerMetrics from '../dashboard/admin/ClaimsManagerMetrics';
import OnboardingSpecialistMetrics from '../dashboard/admin/OnboardingSpecialistMetrics';
import OnboardingSupportBoard from '../operations/onboardingSupport/BoardPage';
import LeadMetricsPage from '../operations/leads/LeadMetricsPage';
import EventsPage from '../operations/leads/events/EventsPage';
import BillingInvoicesPage from '../billing/invoices/BillingInvoicesPage';
import BillingSettingsPage from '../billing/settings/BillingSettingsPage';
import VaRunBoardPage from '../billing/vaRuns/VaRunBoardPage';
import EnvoyDashboardPage from '../billing/envoy/EnvoyDashboardPage';
import MyKpiDashboard from '../dashboard/manager/MyKpiDashboard';
import PartnersPage from '../operations/partners/PartnersPage';
import ReachOut from '../reachOut/ReachOut';
import ReferralForm from '../referral/ReferralForm';
import PartnerSignup from '../referral/PartnerSignup';
import RcCallback from '../ringcentral/RcCallback';
import Support from '../support/Support';
import SupportFormsManager from '../admin/support/SupportFormsManager';
import SupportFormFieldsEditor from '../admin/support/SupportFormFieldsEditor';
import ProviderSettings from '../provider/ProviderSettings';
import RoleManagement from '../admin/RoleManagement';
import CoverageMap from '../coverage-map/CoverageMap';

import EtchCallback from '../callbacks/EtchCallback';

import PrivateRoute from './PrivateRoute';
import PortalRoute from './PortalRoute';
import PortalLogin from '../portal/pages/PortalLogin';
import PortalHome from '../portal/pages/PortalHome';
import PortalReferrals from '../portal/pages/PortalReferrals';
import PortalPayouts from '../portal/pages/PortalPayouts';
import PortalLink from '../portal/pages/PortalLink';
import PortalStatus from '../portal/pages/PortalStatus';

// Set axios base URL at module level so it's available before any component renders
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// Local-dev only (see the /r/:code route comment): forward a QR scan to the
// Express API, which logs the scan event and 302s back to /partner/:slug.
// Derives the API origin from REACT_APP_API_URL (strip the /api suffix).
const ScanRedirectBounce = () => {
  const code = window.location.pathname.split('/r/')[1] || '';
  React.useEffect(() => {
    const apiOrigin = String(process.env.REACT_APP_API_URL || '').replace(
      /\/api\/?$/,
      '',
    );
    if (apiOrigin && code) {
      window.location.replace(`${apiOrigin}/r/${encodeURIComponent(code)}`);
    }
  }, [code]);
  return null;
};

// After sign-in (Nic 2026-10-09): each role lands on its own page
// (src/lib/homePath.js). A deep link still wins: Login and Verify only come
// here when nothing was stashed.
const StartRedirect = () => {
  const { user } = useUser();
  if (!user) return <Navigate to="/sign-in" replace />;
  return <Navigate to={homePathFor(user)} replace />;
};

// The old claim view (Nic 2026-10-09): every link lands on the claim
// profile instead.
const OldClaimRedirect = () => {
  const { claimId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/admin/profile/${claimId}${search}`} replace />;
};

const MainRouter = () => {
  const { signOut, user } = useUser();
  const { portalSignOut } = usePortal();
  const interceptorsSetup = useRef(false);

  // Store signOut in a ref so the interceptor always calls the latest version
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const portalSignOutRef = useRef(portalSignOut);
  portalSignOutRef.current = portalSignOut;

  if (!interceptorsSetup.current) {
    interceptorsSetup.current = true;

    axios.interceptors.request.use(
      function (config) {
        // we use the token here instead of from useUser above because of
        // a caching issue with the function
        const token = getCookie('token') || null;

        // Only add auth headers to our API requests (relative URLs starting with /).
        // Skip for external URLs like S3 presigned URLs — S3 rejects requests
        // that have both query-string auth and an Authorization header.
        const isApiRequest = !config.url || config.url.startsWith('/');
        // Partner Portal requests carry the PORTAL session token, never the app
        // JWT — the two auth systems are fully isolated (see PortalContext).
        // '/portal/' (with slash): a future app endpoint like '/portal-admin' must
        // not be captured. Every real portal call is '/portal/...'.
        const isPortalRequest = Boolean(
          config.url && config.url.startsWith('/portal/'),
        );

        if (isPortalRequest) {
          const portalToken = getCookie(PORTAL_TOKEN_COOKIE) || null;
          if (portalToken) {
            config.headers.Authorization = `Bearer ${portalToken}`;
          }
        } else if (token && isApiRequest) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (isApiRequest) {
          config.headers['X-USER-TIMEZONE'] = moment.tz.guess();
        }

        return config;
      },
      function (err) {
        return Promise.reject(err);
      },
    );

    axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (err) {
        if (err.response?.data?.msg) {
          err.message = err.response.data.msg;
        }

        const isPortalRequest = Boolean(
          err.config?.url && err.config.url.startsWith('/portal/'),
        );

        // Only sign out on 401 (Unauthorized / expired token). A portal 401
        // clears ONLY the portal session (PortalRoute then redirects to the
        // portal login) — it must never touch the app session, and vice versa.
        // EXCEPT rc_login_required (2026-08-11): that 401 means the
        // BROWSER's RingCentral sign-in expired, not the app session — the
        // RC chip/toasts handle it. Signing out of the whole site because
        // a phone token went stale logged Nic out mid-test.
        if (
          err.response?.status === 401 &&
          err.response?.data?.code !== 'rc_login_required'
        ) {
          if (isPortalRequest) portalSignOutRef.current();
          else signOutRef.current();
        }

        // 403 (Forbidden) means user is authenticated but lacks permission - show toast.
        // Portal 403s are handled by portal routing (pending payees live on the
        // status screen) — the app's permission toast would be noise there.
        // Prefer the server's own message when it sent one (business-rule
        // 403s like "claim this client first" beat the generic line).
        // The RBAC middleware's bare "Forbidden" is not a sentence for people.
        if (err.response?.status === 403 && !isPortalRequest) {
          const serverMsg = err.response?.data?.msg;
          const shown =
            serverMsg && serverMsg !== 'Forbidden'
              ? serverMsg
              : 'Permission denied. Contact support if this is unexpected.';
          // Keyed by its words (lib/toastDedupe): the button that made the
          // call toasting the same message doesn't show a second copy.
          toast.error(shown, { toastId: errorToastId(shown), autoClose: 5000 });
        }

        return Promise.reject(err);
      },
    );
  }

  return (
    <BrowserRouter>
      {/* App-level owner of the board's live call: the Call Command Center
          survives navigation to any page and can minimize to a dock. Renders
          nothing while idle; every other calling surface (RcCallModal) is
          untouched. */}
      <CallCenterProvider user={user}>
        <Routes>
          <Route path="/sign-in" element={<Login />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/auth/magic" element={<MagicLinkHandler />} />
          <Route path="/register" element={<RegisterV2 />} />
          {/* The legacy form let a veteran type their own current rating. Gone; old
              bookmarks land on the live flow. */}
          <Route
            path="/register-legacy"
            element={<Navigate to="/register" replace />}
          />
          <Route path="/referral" element={<ReferralForm />} />
          <Route path="/partner-signup" element={<PartnerSignup />} />
          <Route path="/referral/:referralId" element={<ReferralLink />} />
          <Route path="/sign/:token" element={<SignDocument />} />
          <Route
            path="/partner-onboarding/:token"
            element={<PartnerOnboarding />}
          />
          {/* Partner QR/link landing — same lead-capture page as events
            (2026-08-20): scans create a LEAD, with "Create your account" as
            the secondary self-serve path. */}
          <Route
            path="/partner/:partnerReferralUrl"
            element={<EventLanding />}
          />
          {/* Event QR landing — the /r/:code redirect target for event partners. */}
          <Route path="/event/:slug" element={<EventLanding />} />
          {/* /r/:code is an EXPRESS route (scan-tracked QR redirect). In prod it
            never reaches the SPA — Express matches it before the static
            catchall. This bounce exists for LOCAL DEV only, where the CRA dev
            server owns every path: forward the scan to the API, which logs it
            and 302s back to /partner/:slug. */}
          <Route path="/r/:code" element={<ScanRedirectBounce />} />
          {/* Partner Portal — isolated payee session (PortalContext), never the
            app auth. PortalRoute pins non-active payees to /portal/status. */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal" element={<PortalRoute as={PortalHome} />} />
          <Route
            path="/portal/referrals"
            element={<PortalRoute as={PortalReferrals} />}
          />
          <Route
            path="/portal/payouts"
            element={<PortalRoute as={PortalPayouts} />}
          />
          <Route
            path="/portal/link"
            element={<PortalRoute as={PortalLink} />}
          />
          <Route
            path="/portal/status"
            element={<PortalRoute as={PortalStatus} />}
          />
          <Route path="/reach-out" element={<ReachOut />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/processing" element={<Processing />} />
          <Route
            path="/processing/agreement"
            element={<PrivateRoute as={ProcessingAgreement} />}
          />

          <Route
            path="/invitations"
            element={
              <PrivateRoute
                as={Invitations}
                requiredPermissions={[
                  'invitations:view_all',
                  'invitations:view_own',
                ]}
              />
            }
          />
          <Route path="/dashboard" element={<PrivateRoute as={Dashboard} />} />
          <Route
            path="/support/:formSlug?"
            element={
              <PrivateRoute as={Support} requiredPermissions="support:submit" />
            }
          />
          <Route
            path="/questionnaire"
            element={<PrivateRoute as={Questionnaire} />}
          />
          <Route
            path="/meeting/:inviteId"
            element={<PrivateRoute as={Call} />}
          />
          <Route
            path="/ring_meeting/:inviteId"
            element={<PrivateRoute as={RingCall} />}
          />
          <Route
            path="/rc-callback"
            element={<PrivateRoute as={RcCallback} />}
          />
          <Route
            path="/calendar"
            element={
              <PrivateRoute
                as={Calendar}
                requiredRoles={[
                  'admin',
                  'claims_admin',
                  'billing_admin',
                  'director',
                  'claims_manager',
                  'onboarding_specialist',
                  'onboarding_manager',
                  'onboarding_support_specialist',
                  'referral',
                  'provider',
                  'it_support',
                ]}
              />
            }
          />
          <Route
            path="/coverage-map"
            element={
              <PrivateRoute
                as={CoverageMap}
                requiredPermissions="coverage_map:view"
              />
            }
          />
          <Route
            path="/provider/settings"
            element={
              <PrivateRoute
                as={ProviderSettings}
                requiredPermissions="provider:self_service"
              />
            }
          />
          <Route
            path="/callback/:fileId"
            element={<PrivateRoute as={EtchCallback} />}
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute as={Users} requiredPermissions="users:view" />
            }
          />
          <Route
            path="/admin/admins"
            element={
              <PrivateRoute
                as={Admins}
                // Manage Trainees (2026-10-30) opens the page to Claims
                // Managers only, with the one In Training action.
                requiredPermissions={['admins:manage', 'trainees:manage']}
              />
            }
          />
          <Route
            path="/operations/partners"
            element={
              <PrivateRoute
                as={PartnersPage}
                requiredPermissions={['partners:view', 'partners:manage']}
              />
            }
          />
          {/* Legacy URL — bookmarks / old links land on the Operations hub. */}
          <Route
            path="/admin/partners"
            element={<Navigate to="/operations/partners" replace />}
          />
          <Route
            path="/admin/invitations"
            element={
              <PrivateRoute
                as={Invitations}
                requiredPermissions={[
                  'invitations:view_all',
                  'invitations:view_own',
                ]}
              />
            }
          />
          <Route
            path="/admin/providers"
            element={
              <PrivateRoute as={Providers} requiredPermissions="users:view" />
            }
          />
          <Route
            path="/admin/va_calls"
            element={
              <PrivateRoute
                as={VaCalls}
                requiredPermissions="reports:financial"
              />
            }
          />
          <Route
            path="/admin/va_payments"
            element={
              <PrivateRoute
                as={VaPayments}
                requiredPermissions="reports:financial"
              />
            }
          />
          <Route
            path="/operations/referral-payouts"
            element={
              <PrivateRoute
                as={ReferralPayouts}
                requiredPermissions={[
                  'referral_payouts:view',
                  'referral_payouts:manage',
                ]}
              />
            }
          />
          <Route
            path="/billing/invoices"
            element={
              <PrivateRoute
                as={BillingInvoicesPage}
                requiredPermissions={['billing:view', 'billing:manage']}
              />
            }
          />
          <Route
            path="/billing/va-runs"
            element={
              <PrivateRoute
                as={VaRunBoardPage}
                requiredPermissions={['billing:view', 'billing:manage']}
              />
            }
          />
          <Route
            path="/billing/envoy"
            element={
              <PrivateRoute
                as={EnvoyDashboardPage}
                requiredPermissions={['envoy_finance:view', 'envoy_finance:manage']}
              />
            }
          />
          <Route
            path="/billing/settings"
            element={
              <PrivateRoute
                as={BillingSettingsPage}
                requiredPermissions="billing:manage"
              />
            }
          />
          <Route
            path="/admin/documents"
            element={
              <PrivateRoute
                as={DocumentsPage}
                requiredPermissions="documents:manage"
              />
            }
          />
          <Route
            path="/admin/va_failed_calls"
            element={
              <PrivateRoute
                as={VaFailedCallsQueue}
                requiredPermissions={[
                  'admin:system_settings',
                  'reports:financial',
                ]}
              />
            }
          />
          <Route
            path="/admin/va-rates"
            element={
              <PrivateRoute
                as={VaRatesManager}
                requiredPermissions="admin:va_rates"
              />
            }
          />
          <Route
            path="/admin/notification-settings"
            element={
              <PrivateRoute
                as={NotificationSettingsPage}
                requiredPermissions="admin:system_settings"
              />
            }
          />
          {/* CM Assignment (2026-10-09): Ready for CM clients to their
              Claims Manager. */}
          <Route
            path="/operations/cm-assignment"
            element={
              <PrivateRoute as={CmAssignmentPage} requiredPermissions="cm_assignment:manage" />
            }
          />
          {/* Cadence Editor (2026-10-23): its own page, admins only. */}
          <Route
            path="/admin/cadence-editor"
            element={
              <PrivateRoute
                as={CadenceEditorPage}
                requiredPermissions={['onboarding_cadence:edit', 'onboarding_cadence:approve']}
              />
            }
          />
          {/* Automations (2026-10-23): the automated texts and auto stall, admins only. */}
          <Route
            path="/admin/automations"
            element={<PrivateRoute as={AutomationsPage} requiredPermissions="messages:manage" />}
          />
          {/* Messages (2026-10-22): every Sales and Onboarding message. */}
          <Route
            path="/operations/messages"
            element={<PrivateRoute as={MessagesPage} requiredPermissions="messages:manage" />}
          />
          {/* Client Onboarding (2026-09-28): signed clients, worked by the
              Onboarding Specialists to Ready for CM. */}
          <Route
            path="/operations/client-onboarding"
            element={
              <PrivateRoute
                as={ClientOnboardingPage}
                // View only reads it too (Nic 2026-10-05).
                requiredPermissions={[
                  'boards:view',
                  'client_onboarding:work',
                  'client_onboarding:manage',
                ]}
              />
            }
          />
          {/* Signups is retired (Nic 2026-10-09): signing up lives on the
              Sales board, and a signed client's agreement is updated from
              Client Onboarding. Old links (Signups, New Clients, Pending
              Clients) land on Client Onboarding. */}
          {['/operations/signups', '/operations/new-clients', '/admin/pending-clients'].map((path) => (
            <Route
              key={path}
              path={path}
              element={<Navigate to="/operations/client-onboarding" replace />}
            />
          ))}
          {/* Lead Handoffs is retired (2026-09-28): the Sales board holds
              Nurture and the people search now. Old bookmarks land there. */}
          <Route
            path="/operations/leads"
            element={<Navigate to="/operations/onboarding-support" replace />}
          />
          <Route
            path="/operations/handoffs"
            element={<Navigate to="/operations/onboarding-support" replace />}
          />
          <Route
            path="/operations/onboarding-support"
            element={
              <PrivateRoute
                as={OnboardingSupportBoard}
                requiredPermissions={[
                  'boards:view',
                  'onboarding_board:work',
                  'onboarding_board:manage',
                ]}
              />
            }
          />
          {/* Event Leads + Long Term Nurture became overlays ON Lead
              Handoffs (2026-08-24, Schedule-calendar pattern); the old
              routes live on as deep links into the overlay. */}
          <Route
            path="/operations/event-leads"
            element={
              <Navigate to="/operations/onboarding-support" replace />
            }
          />
          <Route
            path="/operations/nurture-leads"
            element={
              <Navigate to="/operations/onboarding-support?view=nurture" replace />
            }
          />
          <Route
            path="/operations/events"
            element={
              <PrivateRoute
                as={EventsPage}
                requiredPermissions={['leads:view', 'leads:manage']}
              />
            }
          />
          <Route
            path="/operations/lead-metrics"
            element={
              <PrivateRoute
                as={LeadMetricsPage}
                requiredPermissions={[
                  'leads:view',
                  'leads:manage',
                  'onboarding_board:metrics',
                ]}
              />
            }
          />
          {/* The standalone metrics page became Lead Metrics' Cadence tab
              (2026-08-23), now the Sales Team tab (2026-10-01). */}
          <Route
            path="/operations/onboarding-metrics"
            element={
              <Navigate to="/operations/lead-metrics?tab=sales" replace />
            }
          />
          <Route
            path="/admin/metrics/claims-managers"
            element={
              <PrivateRoute
                as={ClaimsManagerMetrics}
                requiredPermissions="reports:claims_metrics"
              />
            }
          />
          <Route
            path="/admin/metrics/onboarding"
            element={
              <PrivateRoute
                as={OnboardingSpecialistMetrics}
                requiredPermissions="reports:onboarding"
              />
            }
          />
          <Route
            path="/my-performance"
            element={
              <PrivateRoute
                as={MyKpiDashboard}
                requiredPermissions={['kpi:view_own', 'kpi:view_all']}
              />
            }
          />
          <Route
            path="/admin/support-forms"
            element={
              <PrivateRoute
                as={SupportFormsManager}
                requiredPermissions="admin:support_forms"
              />
            }
          />
          <Route
            path="/admin/support-forms/:formId/fields"
            element={
              <PrivateRoute
                as={SupportFormFieldsEditor}
                requiredPermissions="admin:support_forms"
              />
            }
          />
          <Route
            path="/admin/roles"
            element={
              <PrivateRoute
                as={RoleManagement}
                requiredPermissions="users:manage_roles"
              />
            }
          />
          <Route
            path="/admin/profile/:claimId"
            element={
              <PrivateRoute
                as={Profile}
                requiredPermissions={[
                  'claims:view_all',
                  'claims:view_assigned',
                ]}
              />
            }
          />
          <Route
            path="/billing/payment-plan/:claimId/setup"
            element={<PrivateRoute as={PaymentPlanSetupPage} />}
          />
          <Route
            path="/billing/payment-plan/:claimId/confirmation"
            element={<PrivateRoute as={PaymentPlanConfirmationPage} />}
          />
          <Route path="/admin/claim/:claimId" element={<OldClaimRedirect />} />
          <Route path="/start" element={<StartRedirect />} />
          <Route
            path="/admin/claim/:claimId/files/:fileId/content"
            element={
              <PrivateRoute
                as={ParsedContentViewer}
                requiredPermissions="admin:casegpt_content"
              />
            }
          />
          <Route
            path="/admin/tools/va-forms/mapper/:formId"
            element={
              <PrivateRoute
                as={FormFieldMapper}
                requiredPermissions="admin:dbq_forms"
              />
            }
          />
          <Route
            path="/admin/tools/va-forms"
            element={
              <PrivateRoute
                as={VaForms}
                requiredPermissions="admin:dbq_forms"
              />
            }
          />
          <Route path="/dbq" element={<PrivateRoute as={DbqWorkbench} />} />
          <Route
            path="/dbq/toe"
            element={
              <PrivateRoute
                as={ToeManager}
                requiredPermissions="admin:dbq_forms"
              />
            }
          />
          <Route
            path="/dbq/:formId"
            element={<PrivateRoute as={DbqFormRenderer} />}
          />
          <Route
            path="/dbq/:dbqFormId/debug"
            element={
              <PrivateRoute
                as={DbqTemplateDebug}
                requiredPermissions="admin:dbq_forms"
              />
            }
          />
          <Route
            path="/dbq/edit/:userDbqId"
            element={<PrivateRoute as={UserDbqEditor} />}
          />
          <Route
            path="/quickbooks/:qbAction"
            element={
              <PrivateRoute
                as={Quickbooks}
                requiredPermissions="admin:quickbooks"
              />
            }
          />

          <Route path="/" element={<Home />} />
        </Routes>
      </CallCenterProvider>
    </BrowserRouter>
  );
};

export default MainRouter;
